// src/modules/Admin/pages/Orders/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FaShoppingCart,
  FaSearch,
  FaEye,
  FaTruck,
  FaCheck,
  FaTimes,
  FaBox
} from 'react-icons/fa';
import { supabase } from '../../../../config/supabase';

const PageContainer = styled.div``;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #111;
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: #00D4AA;
    }
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 200ms;
  border-left: 3px solid ${props => props.$color || '#ddd'};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  ${props => props.$active && `
    background: ${props.$color}10;
    border-color: ${props.$color};
  `}

  h4 {
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  p {
    font-size: 24px;
    font-weight: 700;
    color: #111;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 400px;
  position: relative;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #00D4AA;
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;
  border-collapse: collapse;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px;
  background: #f8f8f8;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  border-bottom: 1px solid #eee;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
`;

const OrderNumber = styled.span`
  font-weight: 600;
  color: #111;
`;

const CustomerInfo = styled.div`
  p {
    font-weight: 500;
    color: #111;
  }

  span {
    font-size: 12px;
    color: #999;
  }
`;

const Price = styled.span`
  font-weight: 600;
  color: #111;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'pending': return 'rgba(245, 158, 11, 0.1)';
      case 'confirmed': return 'rgba(59, 130, 246, 0.1)';
      case 'processing': return 'rgba(139, 92, 246, 0.1)';
      case 'shipped': return 'rgba(6, 182, 212, 0.1)';
      case 'delivered': return 'rgba(34, 197, 94, 0.1)';
      case 'cancelled': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(156, 163, 175, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'processing': return '#8b5cf6';
      case 'shipped': return '#06b6d4';
      case 'delivered': return '#22c55e';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

const PaymentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$paid ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
  color: ${props => props.$paid ? '#22c55e' : '#f59e0b'};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 200ms;
  color: #666;

  &:hover {
    border-color: #00D4AA;
    background: rgba(0, 212, 170, 0.05);
    color: #00D4AA;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    color: #666;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 18px;
    font-weight: 600;
  }

  button {
    background: none;
    border: none;
    font-size: 20px;
    color: #666;
    cursor: pointer;

    &:hover {
      color: #111;
    }
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const OrderDetail = styled.div`
  margin-bottom: 24px;

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: #666;
    margin-bottom: 12px;
    text-transform: uppercase;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  label {
    display: block;
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    color: #111;
    font-weight: 500;
  }
`;

const OrderItems = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 6px;
    background: #f5f5f5;
  }

  .info {
    flex: 1;

    p {
      font-weight: 500;
      margin-bottom: 4px;
    }

    span {
      font-size: 12px;
      color: #666;
    }
  }

  .price {
    font-weight: 600;
    color: #111;
  }
`;

const StatusSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  margin-top: 8px;

  &:focus {
    outline: none;
    border-color: #00D4AA;
  }
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const statusLabels = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  processing: 'Processando',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado'
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0
  });

  useEffect(() => {
    fetchOrders();
  }, [search, statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('shop_orders')
        .select(`
          *,
          customer:shop_customers(name, email),
          items:shop_order_items(*)
        `)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];

      if (search) {
        filteredData = filteredData.filter(order =>
          order.order_number?.toString().includes(search) ||
          order.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
          order.customer?.email?.toLowerCase().includes(search.toLowerCase())
        );
      }

      setOrders(filteredData);

      // Stats
      const allOrders = data || [];
      setStats({
        total: allOrders.length,
        pending: allOrders.filter(o => o.status === 'pending').length,
        processing: allOrders.filter(o => o.status === 'processing').length,
        shipped: allOrders.filter(o => o.status === 'shipped').length,
        delivered: allOrders.filter(o => o.status === 'delivered').length
      });
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const updates = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'shipped') {
        updates.shipped_at = new Date().toISOString();
      } else if (newStatus === 'delivered') {
        updates.delivered_at = new Date().toISOString();
      }

      await supabase
        .from('shop_orders')
        .update(updates)
        .eq('id', orderId);

      fetchOrders();

      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <h1>
          <FaShoppingCart /> Pedidos
        </h1>
      </PageHeader>

      <Stats>
        <StatCard
          $color="#6b7280"
          $active={statusFilter === ''}
          onClick={() => setStatusFilter('')}
        >
          <h4>Todos</h4>
          <p>{stats.total}</p>
        </StatCard>
        <StatCard
          $color="#f59e0b"
          $active={statusFilter === 'pending'}
          onClick={() => setStatusFilter('pending')}
        >
          <h4>Pendentes</h4>
          <p>{stats.pending}</p>
        </StatCard>
        <StatCard
          $color="#8b5cf6"
          $active={statusFilter === 'processing'}
          onClick={() => setStatusFilter('processing')}
        >
          <h4>Processando</h4>
          <p>{stats.processing}</p>
        </StatCard>
        <StatCard
          $color="#06b6d4"
          $active={statusFilter === 'shipped'}
          onClick={() => setStatusFilter('shipped')}
        >
          <h4>Enviados</h4>
          <p>{stats.shipped}</p>
        </StatCard>
        <StatCard
          $color="#22c55e"
          $active={statusFilter === 'delivered'}
          onClick={() => setStatusFilter('delivered')}
        >
          <h4>Entregues</h4>
          <p>{stats.delivered}</p>
        </StatCard>
      </Stats>

      <Toolbar>
        <SearchBox>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por pedido, cliente ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchBox>
      </Toolbar>

      {orders.length === 0 && !loading ? (
        <EmptyState>
          <FaBox />
          <h3>Nenhum pedido encontrado</h3>
          <p>Os pedidos aparecerao aqui quando os clientes comprarem</p>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Pedido</Th>
              <Th>Cliente</Th>
              <Th>Total</Th>
              <Th>Pagamento</Th>
              <Th>Status</Th>
              <Th>Data</Th>
              <Th>Acoes</Th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <Td>
                  <OrderNumber>#{order.order_number}</OrderNumber>
                </Td>
                <Td>
                  <CustomerInfo>
                    <p>{order.customer?.name || 'Cliente'}</p>
                    <span>{order.customer?.email}</span>
                  </CustomerInfo>
                </Td>
                <Td>
                  <Price>{formatPrice(order.total)}</Price>
                </Td>
                <Td>
                  <PaymentBadge $paid={order.payment_status === 'paid'}>
                    {order.payment_status === 'paid' ? (
                      <><FaCheck /> Pago</>
                    ) : (
                      'Pendente'
                    )}
                  </PaymentBadge>
                </Td>
                <Td>
                  <StatusBadge $status={order.status}>
                    {statusLabels[order.status] || order.status}
                  </StatusBadge>
                </Td>
                <Td>{formatDate(order.created_at)}</Td>
                <Td>
                  <Actions>
                    <ActionButton onClick={() => setSelectedOrder(order)} title="Ver detalhes">
                      <FaEye />
                    </ActionButton>
                  </Actions>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {selectedOrder && (
        <Modal onClick={() => setSelectedOrder(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Pedido #{selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)}>
                <FaTimes />
              </button>
            </ModalHeader>

            <ModalBody>
              <OrderDetail>
                <h3>Informacoes do Pedido</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>Status</label>
                    <StatusBadge $status={selectedOrder.status}>
                      {statusLabels[selectedOrder.status]}
                    </StatusBadge>
                    <StatusSelect
                      value={selectedOrder.status}
                      onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                    >
                      <option value="pending">Pendente</option>
                      <option value="confirmed">Confirmado</option>
                      <option value="processing">Processando</option>
                      <option value="shipped">Enviado</option>
                      <option value="delivered">Entregue</option>
                      <option value="cancelled">Cancelado</option>
                    </StatusSelect>
                  </DetailItem>
                  <DetailItem>
                    <label>Pagamento</label>
                    <PaymentBadge $paid={selectedOrder.payment_status === 'paid'}>
                      {selectedOrder.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                    </PaymentBadge>
                    <p style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                      {selectedOrder.payment_method === 'pix' ? 'Pix' : 'Cartao'}
                    </p>
                  </DetailItem>
                  <DetailItem>
                    <label>Data do Pedido</label>
                    <p>{formatDate(selectedOrder.created_at)}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Total</label>
                    <p style={{ fontSize: '18px', color: '#00D4AA' }}>
                      {formatPrice(selectedOrder.total)}
                    </p>
                  </DetailItem>
                </DetailGrid>
              </OrderDetail>

              <OrderDetail>
                <h3>Cliente</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>Nome</label>
                    <p>{selectedOrder.customer?.name || '-'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Email</label>
                    <p>{selectedOrder.customer?.email || '-'}</p>
                  </DetailItem>
                </DetailGrid>
              </OrderDetail>

              {selectedOrder.shipping_address && (
                <OrderDetail>
                  <h3>Endereco de Entrega</h3>
                  <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                    {selectedOrder.shipping_address.street}, {selectedOrder.shipping_address.number}
                    {selectedOrder.shipping_address.complement && ` - ${selectedOrder.shipping_address.complement}`}
                    <br />
                    {selectedOrder.shipping_address.neighborhood} - {selectedOrder.shipping_address.city}/{selectedOrder.shipping_address.state}
                    <br />
                    CEP: {selectedOrder.shipping_address.zip_code}
                  </p>
                </OrderDetail>
              )}

              <OrderDetail>
                <h3>Itens do Pedido</h3>
                <OrderItems>
                  {selectedOrder.items?.map((item, index) => (
                    <OrderItem key={index}>
                      <img
                        src={item.product_image || 'https://placehold.co/48x48/f5f5f5/999?text=P'}
                        alt={item.product_name}
                      />
                      <div className="info">
                        <p>{item.product_name}</p>
                        <span>{item.quantity}x {formatPrice(item.unit_price)}</span>
                      </div>
                      <div className="price">{formatPrice(item.total_price)}</div>
                    </OrderItem>
                  ))}
                </OrderItems>
              </OrderDetail>

              <OrderDetail>
                <h3>Resumo</h3>
                <div style={{ background: '#f8f8f8', padding: '16px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Subtotal</span>
                    <span>{formatPrice(selectedOrder.subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Frete</span>
                    <span>{formatPrice(selectedOrder.shipping_cost || 0)}</span>
                  </div>
                  {selectedOrder.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#22c55e' }}>
                      <span>Desconto</span>
                      <span>-{formatPrice(selectedOrder.discount)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '18px', borderTop: '1px solid #ddd', paddingTop: '12px', marginTop: '8px' }}>
                    <span>Total</span>
                    <span>{formatPrice(selectedOrder.total)}</span>
                  </div>
                </div>
              </OrderDetail>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Orders;
